// src/components/ClientForm.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { AddressItem, DateItem, GeneralItem, IntroductionItem, Item, NoteItem, Section, SongItem, TimelineItem } from './types';
import ItemEditor from './ItemEditor';
import { fetchFormById, updateFormById } from '../api';

interface OldFormData {
  firstDanceSong: string;
  timeline: { event: string; time: string }[];
}

const ClientForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<any>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [oldFormData, setOldFormData] = useState<OldFormData | null>(null);
  const [selectedItemTypes, setSelectedItemTypes] = useState<{ [sectionIndex: number]: 'song' | 'introduction' | 'timeline' | 'general' | 'address' | 'date' | 'note' }>({});

  useEffect(() => {
    const token = localStorage.getItem('clientToken');
    if (!token) {
      navigate('/client/login');
      return;
    }
    fetchForm(token);
  }, [id, navigate]);

  const fetchForm = async (token: string) => {
    try {
      const response = await fetchFormById(parseInt(id!), token);
      setForm(response);
      const jsonData = JSON.parse(response.jsonData || '{}');

      if (jsonData.sections) {
        const initializedSections = jsonData.sections.map((section: Section) => ({
          ...section,
          clientNote: section.clientNote || '',
          allowClientAdditions: section.allowClientAdditions ?? false,
          allowedItemTypes: section.allowedItemTypes ?? [],
          items: section.items.map((item: Item) => {
            const baseItem = {
              ...item,
              clientNotes: item.clientNotes || '',
              isClientAdded: item.isClientAdded ?? false,
            };
            switch (item.type) {
              case 'song':
                return { ...baseItem, label: (item as SongItem).label || '', song: (item as SongItem).song || '' };
              case 'introduction':
                return { ...baseItem, name: (item as IntroductionItem).name || '', song: (item as IntroductionItem).song || '' };
              case 'timeline':
                return { ...baseItem, event: (item as TimelineItem).event || '', time: (item as TimelineItem).time || '' };
              case 'general':
                return { ...baseItem, key: (item as GeneralItem).key || '', value: (item as GeneralItem).value || '' };
              case 'address':
                return {
                  ...baseItem,
                  label: (item as AddressItem).label || '',
                  street: (item as AddressItem).street || '',
                  city: (item as AddressItem).city || '',
                  state: (item as AddressItem).state || '',
                  zip: (item as AddressItem).zip || ''
                };
              case 'date':
                return {
                  ...baseItem,
                  label: (item as DateItem).label || '',
                  date: (item as DateItem).date || '',
                  time: (item as DateItem).time || ''
                };
              case 'note':
                return {
                  ...baseItem,
                  content: (item as NoteItem).content || '',
                  height: (item as NoteItem).height || 100
                };
              default:
                return baseItem;
            }
          }),
        }));
        setSections(initializedSections);
      } else if (jsonData.firstDanceSong || jsonData.timeline) {
        const convertedSections: Section[] = [];
        if (jsonData.firstDanceSong !== undefined) {
          convertedSections.push({
            label: 'First Dance',
            note: '',
            clientNote: '',
            allowClientAdditions: false,
            allowedItemTypes: [],
            items: [{ type: 'song', label: 'First Dance Song', song: jsonData.firstDanceSong, notes: '', clientNotes: '', isClientAdded: false }],
          });
        }
        if (jsonData.timeline && jsonData.timeline.length > 0) {
          convertedSections.push({
            label: 'Timeline',
            note: '',
            clientNote: '',
            allowClientAdditions: false,
            allowedItemTypes: [],
            items: jsonData.timeline.map((event: any) => ({
              type: 'timeline',
              event: event.event,
              time: event.time,
              notes: '',
              clientNotes: '',
              isClientAdded: false,
            })),
          });
        }
        setSections(convertedSections);
        setOldFormData(jsonData);
      } else {
        setSections([]);
      }
    } catch (error) {
      console.error('Failed to fetch form:', error);
      alert('Failed to load form. Check the link or try again.');
    }
  };

  const updateForm = async () => {
    try {
      const token = localStorage.getItem('clientToken');
      if (!token) throw new Error('No client authentication token found.');

      const updatedData = oldFormData
        ? {
            firstDanceSong: sections.find(s => s.label === 'First Dance')?.items[0]?.type === 'song'
              ? (sections.find(s => s.label === 'First Dance')?.items[0] as SongItem).song || ''
              : '',
            timeline: sections
              .find(s => s.label === 'Timeline')
              ?.items.map(item => ({
                event: (item as TimelineItem).event || '',
                time: (item as TimelineItem).time || '',
              })) || [],
          }
        : { sections };
      await updateFormById(parseInt(id!), JSON.stringify(updatedData), 'submitted', token);
      alert('Form submitted successfully!');
      setForm({ ...form, status: 'submitted' });
    } catch (error) {
      console.error('Failed to update form:', error);
      alert('Failed to submit form. Please try again.');
    }
  };

  const updateItemField = (sectionIndex: number, itemIndex: number, field: string, value: string) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].items[itemIndex] = { ...updatedSections[sectionIndex].items[itemIndex], [field]: value };
    setSections(updatedSections);
  };

  const updateSectionClientNote = (sectionIndex: number, clientNote: string) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].clientNote = clientNote;
    setSections(updatedSections);
  };

  const addItem = (sectionIndex: number, itemType: 'song' | 'introduction' | 'timeline' | 'general' | 'address' | 'date' | 'note') => {
    const updatedSections = [...sections];
    switch (itemType) {
      case 'song':
        updatedSections[sectionIndex].items.push({ type: 'song', label: 'Client Added Song', song: '', notes: '', clientNotes: '', isClientAdded: true });
        break;
      case 'introduction':
        updatedSections[sectionIndex].items.push({ type: 'introduction', name: '', song: '', notes: '', clientNotes: '', isClientAdded: true });
        break;
      case 'timeline':
        updatedSections[sectionIndex].items.push({ type: 'timeline', event: '', time: '', notes: '', clientNotes: '', isClientAdded: true });
        break;
      case 'general':
        updatedSections[sectionIndex].items.push({ type: 'general', key: 'New Field', value: '', notes: '', clientNotes: '', isClientAdded: true });
        break;
      case 'address':
        updatedSections[sectionIndex].items.push({ type: 'address', label: 'Client Added Address', street: '', city: '', state: '', zip: '', notes: '', clientNotes: '', isClientAdded: true });
        break;
      case 'date':
        updatedSections[sectionIndex].items.push({ type: 'date', label: 'Client Added Date', date: '', time: '', notes: '', clientNotes: '', isClientAdded: true });
        break;
      case 'note':
        updatedSections[sectionIndex].items.push({ type: 'note', content: '', height: 100, notes: '', clientNotes: '', isClientAdded: true });
        break;
    }
    setSections(updatedSections);
  };

  const deleteItem = (sectionIndex: number, itemIndex: number) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].items.splice(itemIndex, 1);
    setSections(updatedSections);
  };

  const clearItemData = (sectionIndex: number, itemIndex: number) => {
    const updatedSections = [...sections];
    const item = updatedSections[sectionIndex].items[itemIndex];
    switch (item.type) {
      case 'song':
        updatedSections[sectionIndex].items[itemIndex] = { ...item, song: '', clientNotes: '' };
        break;
      case 'introduction':
        updatedSections[sectionIndex].items[itemIndex] = { ...item, name: '', song: '', clientNotes: '' };
        break;
      case 'timeline':
        updatedSections[sectionIndex].items[itemIndex] = { ...item, event: '', time: '', clientNotes: '' };
        break;
      case 'general':
        updatedSections[sectionIndex].items[itemIndex] = { ...item, value: '', clientNotes: '' };
        break;
      case 'address':
        updatedSections[sectionIndex].items[itemIndex] = { ...item, street: '', city: '', state: '', zip: '', clientNotes: '' };
        break;
      case 'date':
        updatedSections[sectionIndex].items[itemIndex] = { ...item, date: '', time: '', clientNotes: '' };
        break;
      case 'note':
        updatedSections[sectionIndex].items[itemIndex] = { ...item, content: '', clientNotes: '' };
        break;
    }
    setSections(updatedSections);
  };

  const handleItemTypeChange = (sectionIndex: number, itemType: 'song' | 'introduction' | 'timeline' | 'general' | 'address' | 'date' | 'note') => {
    setSelectedItemTypes({ ...selectedItemTypes, [sectionIndex]: itemType });
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    if (!destination) return;
    if (source.droppableId !== destination.droppableId) return;
    if (source.index === destination.index) return;

    const sectionIndex = parseInt(source.droppableId.split('-')[1], 10);
    const updatedSections = [...sections];
    const sectionItems = [...updatedSections[sectionIndex].items];

    const [movedItem] = sectionItems.splice(source.index, 1);
    sectionItems.splice(destination.index, 0, movedItem);

    updatedSections[sectionIndex].items = sectionItems;
    setSections(updatedSections);
  };

  if (!form) return <div className="container mt-5"><div className="alert alert-info">Loading...</div></div>;

  return (
    <div className="container mt-5">
      <h2 className="mb-4 text-center">Planning Form</h2>
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <p className="card-text"><strong>Status:</strong> {form.status || 'Not set'}</p>
          {form.status === 'submitted' ? (
            <div className="alert alert-success text-center">
              This form has been submitted. Thank you!
            </div>
          ) : (
            <>
              {sections.length === 0 ? (
                <div className="alert alert-warning text-center">
                  No sections available to fill out.
                </div>
              ) : (
                <DragDropContext onDragEnd={onDragEnd}>
                  {sections.map((section, sectionIndex) => (
                    <div key={sectionIndex} className="card mb-3 shadow-sm">
                      <div className="card-header bg-primary text-white">
                        <h4 className="mb-0">{section.label}</h4>
                      </div>
                      <div className="card-body">
                        {section.note && (
                          <p className="text-muted"><strong>DJ Note:</strong> {section.note}</p>
                        )}
                        <div className="mb-3">
                          <label className="form-label">
                            <strong>General Note for {section.label}:</strong>
                          </label>
                          <textarea
                            className="form-control"
                            value={section.clientNote}
                            onChange={(e) => updateSectionClientNote(sectionIndex, e.target.value)}
                            placeholder="e.g., Ceremony will be held outside unless it rains"
                            rows={3}
                          />
                        </div>
                        <Droppable droppableId={`section-${sectionIndex}`}>
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                              {section.items.map((item, itemIndex) => (
                                <Draggable
                                  key={`${sectionIndex}-${itemIndex}`}
                                  draggableId={`${sectionIndex}-${itemIndex}`}
                                  index={itemIndex}
                                >
                                  {(provided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className="draggable-item d-flex flex-column flex-md-row align-items-md-center mb-3 p-3 border rounded bg-light"
                                    >
                                      <ItemEditor
                                        item={item}
                                        sectionIndex={sectionIndex}
                                        itemIndex={itemIndex}
                                        updateItemField={updateItemField}
                                        isClientView={true}
                                      />
                                      <div className="d-flex align-items-center mt-2 mt-md-0">
                                        {item.isClientAdded ? (
                                          <button
                                            onClick={() => deleteItem(sectionIndex, itemIndex)}
                                            className="btn btn-danger btn-sm ms-2"
                                          >
                                            Delete Item
                                          </button>
                                        ) : (
                                          <button
                                            onClick={() => clearItemData(sectionIndex, itemIndex)}
                                            className="btn btn-outline-secondary btn-sm ms-2"
                                          >
                                            Clear
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                        {section.allowClientAdditions && section.allowedItemTypes.length > 0 && (
                          <div className="d-flex align-items-center mt-2">
                            {section.allowedItemTypes.length > 1 ? (
                              <>
                                <select
                                  className="form-select me-2"
                                  style={{ width: '200px' }}
                                  value={selectedItemTypes[sectionIndex] || section.allowedItemTypes[0]}
                                  onChange={(e) => handleItemTypeChange(sectionIndex, e.target.value as 'song' | 'introduction' | 'timeline' | 'general' | 'address' | 'date' | 'note')}
                                >
                                  {section.allowedItemTypes.map(type => (
                                    <option key={type} value={type}>
                                      {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  onClick={() => addItem(sectionIndex, selectedItemTypes[sectionIndex] || section.allowedItemTypes[0])}
                                  className="btn btn-outline-primary btn-sm"
                                >
                                  Add Item
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => addItem(sectionIndex, section.allowedItemTypes[0])}
                                className="btn btn-outline-primary btn-sm"
                              >
                                Add {section.allowedItemTypes[0].charAt(0).toUpperCase() + section.allowedItemTypes[0].slice(1)}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </DragDropContext>
              )}
              <div className="text-center">
                <button onClick={updateForm} className="btn btn-success btn-lg">
                  Submit Form
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientForm;